import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Transition,
} from 'framer-motion';
import Loading from '../components/Loading';
import { verifyPasswordToken, resetPassword } from '../api/endpoints';
import { VerificationExpiredError } from '../api/config';
import { navigateTo } from '../utils/navigation';
import { PASS_RE } from '../utils/regex';

const EASE = [0.22, 0.61, 0.36, 1] as const;

export default function ResetPasswordPage(): React.JSX.Element {
  const params = new URLSearchParams(useLocation().search);
  const token = params.get('token');

  type ViewState = 'loading' | 'invalid' | 'ready' | 'done';
  const [state, setState] = useState<ViewState>('loading');
  const [accountEmail, setAccountEmail] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState({ password: false, confirm: false });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const prefersReduced = useReducedMotion();
  const fadeQuick: Transition = prefersReduced
    ? { duration: 0 }
    : { duration: 0.25, ease: EASE };

  const hasLen    = password.length >= 8 && password.length <= 64;
  const hasUpper  = /[A-Z]/.test(password);
  const hasLower  = /[a-z]/.test(password);
  const hasDigit  = /\d/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  const passValid    = PASS_RE.test(password);
  const confirmValid = confirm.length > 0 && confirm === password;

  const showPassErr    = touched.password && !passValid && password.length > 0;
  const showConfirmErr = touched.confirm && !confirmValid && confirm.length > 0;
  const canSubmit      = state === 'ready' && passValid && confirmValid && !submitting;

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    void (async () => {
      try {
        const res = await verifyPasswordToken(token);
        if (res.verified) {
          if (res.email) setAccountEmail(res.email);
          setState('ready');
        } else {
          setState('invalid');
        }
      } catch {
        setState('invalid');
      }
    })();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setTouched({ password: true, confirm: true });
    if (!token || !canSubmit) return;
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setState('done');
    } catch (error_: unknown) {
      if (error_ instanceof VerificationExpiredError) {
        setState('invalid');
      } else {
        setError('Could not reset your password. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'loading') return <Loading />;

  const shell = (children: React.ReactNode) => (
    <div
      className="relative min-h-dvh flex items-center justify-center p-4"
      style={{ background: 'var(--bg)' }}
    >
      <div
        className="orb"
        aria-hidden
        style={{
          position: 'absolute',
          bottom: '-80px',
          right: '-80px',
          width: 'clamp(240px, 30vw, 440px)',
          opacity: 0.45,
          pointerEvents: 'none',
        }}
      />
      <div style={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </div>
  );

  if (state === 'done') {
    return shell(
      <div className="card stack-5" style={{ textAlign: 'center' }}>
        <div className="stack-2">
          <h1 className="t-h3">Password updated</h1>
          <p className="t-small t-mute" style={{ margin: 0 }}>
            Your password has been changed successfully
            {accountEmail && (
              <> for <span style={{ fontWeight: 500, color: 'var(--ink)' }}>{accountEmail}</span></>
            )}.
          </p>
        </div>
        <button className="btn btn--primary btn--full" onClick={() => { navigateTo('/login'); }}>
          Go to login
        </button>
      </div>,
    );
  }

  if (state === 'invalid') {
    return shell(
      <div className="card stack-5" style={{ textAlign: 'center' }}>
        <div className="stack-2">
          <h1 className="t-h3">Link invalid or expired</h1>
          <p className="t-small t-mute" style={{ margin: 0 }}>
            This password reset link is no longer valid. Request a new one and try again.
          </p>
        </div>
        <button className="btn btn--primary btn--full" onClick={() => { navigateTo('/forgot-password'); }}>
          Request a new link
        </button>
      </div>,
    );
  }

  // state === 'ready'
  return shell(
    <div className="card stack-5">
      <div className="stack-2">
        <h1 className="t-h3">Set a new password</h1>
        {accountEmail && (
          <p className="t-small t-mute" style={{ margin: 0 }}>
            for <span style={{ fontWeight: 500, color: 'var(--ink)' }}>{accountEmail}</span>
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="stack-3" noValidate>
        <div className="stack-2">
          <input
            className={`field${showPassErr ? ' field--error' : ''}`}
            type="password"
            placeholder="New password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); }}
            onBlur={() => { setTouched((t) => ({ ...t, password: true })); }}
            minLength={8}
            maxLength={64}
            title="8–64 characters, include upper & lower case, a number, and a symbol"
            aria-invalid={showPassErr ? 'true' : 'false'}
            autoComplete="new-password"
            required
          />
          <AnimatePresence initial={false}>
            {showPassErr && (
              <motion.p
                key="pass-err"
                className="notice notice--error"
                role="alert"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={fadeQuick}
                style={{ margin: 0 }}
              >
                Use 8–64 characters with upper and lower case, a number, and a symbol.
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <div className="stack-2">
          <input
            className={`field${showConfirmErr ? ' field--error' : ''}`}
            type="password"
            placeholder="Confirm password"
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); }}
            onBlur={() => { setTouched((t) => ({ ...t, confirm: true })); }}
            minLength={8}
            maxLength={64}
            aria-invalid={showConfirmErr ? 'true' : 'false'}
            autoComplete="new-password"
            required
          />
          <AnimatePresence initial={false}>
            {showConfirmErr && (
              <motion.p
                key="confirm-err"
                className="notice notice--error"
                role="alert"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={fadeQuick}
                style={{ margin: 0 }}
              >
                Passwords do not match.
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <div className="pass-checklist">
          <p className="t-label" style={{ margin: 0 }}>Password must include</p>
          <div className="pass-checklist__grid">
            <CheckItem ok={hasLen}    label="8–64 characters" />
            <CheckItem ok={hasUpper}  label="Uppercase letter" />
            <CheckItem ok={hasLower}  label="Lowercase letter" />
            <CheckItem ok={hasDigit}  label="Number" />
            <CheckItem ok={hasSymbol} label="Symbol" />
          </div>
        </div>

        <AnimatePresence initial={false}>
          {error && (
            <motion.p
              key="server-err"
              className="notice notice--error"
              role="alert"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={fadeQuick}
              style={{ margin: 0 }}
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        <button type="submit" className="btn btn--primary btn--full" disabled={!canSubmit}>
          {submitting ? 'Updating…' : 'Set password'}
        </button>
      </form>

      <p className="t-small t-mute" style={{ margin: 0, textAlign: 'center' }}>
        Remembered it?{' '}
        <button type="button" onClick={() => { navigateTo('/login'); }} className="link">
          Go to login
        </button>
      </p>
    </div>,
  );
}

function CheckItem({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className={`check-item${ok ? ' check-item--ok' : ''}`}>
      <span className="check-item__icon" aria-hidden="true">
        <svg viewBox="0 0 12 10" width="10" height="10" fill="none">
          <path
            d="M1 5l3 3 7-7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="check-item__label">{label}</span>
    </div>
  );
}
